import { Range, Editor } from 'domains/reporter/RichTextEditor/core';
import type { Emitter } from 'mitt';
import type { SetterOrUpdater } from 'recoil';
import type { OnExternalSubstitution } from '../types';
import type {
  AdditionalParams,
  NvoqWebsocketConfig,
  NvoqDoneMessage,
  EmitterEvent,
  SelectionEventHandler,
  TextEventHandler,
  NvoqError,
} from './createNvoqWebsocketFactory';
import type { StepEntry } from './useSteps';
import type {
  RecorderDataAvailableEvent,
  RecorderEventPayloadMap,
  RecorderSilenceDetectedEvent,
  RecorderStopRecordingEvent,
} from 'common/Recorder/useRecorder/useRecorder';
import type { ReportStatuses } from 'domains/reporter/Reporter/state';

import { useRangeMasksDispatch } from '../../../hooks';
import { createEditorWarning, partialEditor } from '../../../utils';
import { MINIMUM_DICTATION_DURATION_MILLIS } from '../constants';
import { DICTATION_PLUGIN_ID } from '../types';
import {
  DictationStableTextManagerMapValue,
  RegisterDictationOptions,
  useTextProcessing,
} from '../useTextProcessing';
import {
  createNvoqBoundaryRequestMessage,
  createNvoqWebsocketFactory,
  NVOQ_DONE_MESSAGE,
} from './createNvoqWebsocketFactory';

import {
  isPCMRecorderDataAvailableEvent,
  recorderEmitter,
  REQUEST_BOUNDARY_DELAY,
} from 'common/Recorder/useRecorder';
import { APP_VERSION } from 'config/constants';
import { env } from 'config/env';
import {
  dictationQueueSizeState,
  PROVISIONAL_SUBMIT_STATUSES,
  reportStatusState,
} from 'domains/reporter/Reporter/state';
import { marks } from 'domains/reporter/RichTextEditor/analytics/performance';
import { sentinelSelection } from 'domains/reporter/RichTextEditor/utils/sentinelSelection';
import { stringifyRange } from 'domains/reporter/RichTextEditor/utils/stringify';
import { microphoneState } from 'domains/reporter/useMicrophone';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useWorklistItemAnalytics } from 'hooks/useWorklistItemAnalytics';
import mitt from 'mitt';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { logger } from 'modules/logger';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import useUnmount from 'react-use/esm/useUnmount';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { performance } from 'utils/performance';
import { telemetryContextFor } from 'modules/Apollo/types';

type MessageToNvoq = Blob | NvoqDoneMessage;
type UseNvoqQueueProps = Readonly<{
  /**
   * The maximum number of connections to open up to nVoq at a given time. They have certain
   * limits per environment.
   */
  maxNumberOfConnections: number;
  editor: Editor;
  isEnabled: boolean;
  isAlwaysEditable?: boolean;
  nvoqID: string;
  nvoqAuthorization: string;
  onWebsocketError: () => void;
  onNvoqError: (e?: { msg: NvoqError }) => void;
  enablePicklistDictation: boolean;
  onExternalSubstitution: OnExternalSubstitution;
}>;

export type DictationQueueMapValue = {
  selection: Range;
  data: Array<MessageToNvoq>;
};

const isNvoqDoneMessage = (message: MessageToNvoq): message is NvoqDoneMessage =>
  typeof message === 'object' &&
  message !== null &&
  (message as Partial<NvoqDoneMessage>).method === 'AUDIODONE';

export type UseNvoqQueue = {
  nvoqEmitter: Emitter<EmitterEvent>;
  nvoqWebsocketMap: Map<string, WebSocket>;
  dictationQueueMap: Map<string, DictationQueueMapValue>;
  pendingWebsocketDictationQueueMap: Map<string, DictationQueueMapValue>;
  dictationStableTextManagerMap: Map<string, DictationStableTextManagerMapValue>;
  selectionToStepsQueueMap: Map<Range, StepEntry[]>;
  selectionToDoneMessageSentRef: Map<string, true>;
};

// In a Provisional Submit State, the editor's selection will naturally be null.
// In order to leverage the maps, we delegate this special selection (not available with natural use).
// Also allows us to not complicate the code with additional null-selection checks.
export const PROVISIONAL_SUBMIT_SELECTION: Range = sentinelSelection('PROVISIONAL_SUBMIT');

const createWorkNextItemInQueue =
  ({
    createNvoqWebsocket,
    dictationQueueMap,
    pendingWebsocketDictationQueueMap,
    nvoqWebsocketMap,
    registerDictation,
    editor,
    onNewWebsocketOpenSendQueue,
    worklistItemId,
    additionalParams,
    setDictationQueueSizeState,
    selectionToStepsQueueMap,
  }: {
    createNvoqWebsocket: (config: NvoqWebsocketConfig) => WebSocket;
    registerDictation: (id: string, options: RegisterDictationOptions) => void;
    dictationQueueMap: Map<
      string,
      {
        selection: Range;
        data: Array<MessageToNvoq>;
      }
    >;
    pendingWebsocketDictationQueueMap: Map<
      string,
      {
        selection: Range;
        data: Array<MessageToNvoq>;
      }
    >;
    nvoqWebsocketMap: Map<string, WebSocket>;
    editor: Editor;
    selectionToStepsQueueMap: Map<Range, StepEntry[]>;
    onNewWebsocketOpenSendQueue: {
      current: Map<
        string,
        {
          selection: Range;
          data: Array<MessageToNvoq>;
        }
      >;
    };
    worklistItemId: string;
    additionalParams: AdditionalParams;
    setDictationQueueSizeState: SetterOrUpdater<{
      pendingDictations: number;
      pendingWebsocketDictations: number;
      openWebsocketConnections: number;
    }>;
  }) =>
  () => {
    let itemToWork:
      | undefined
      | [
          string,
          {
            selection: Range;
            data: Array<MessageToNvoq>;
          },
        ] = undefined;
    let itemToWorkId: undefined | string = undefined;
    let itemToWorkSelection: undefined | Range = undefined;
    let itemToWorkAudioBlobs: undefined | Array<MessageToNvoq> = undefined;

    // Pull the first item from the dictation queue
    itemToWork = dictationQueueMap.entries().next().value;

    if (itemToWork == null) {
      return;
    }

    itemToWorkId = itemToWork[0];
    itemToWorkSelection = itemToWork[1].selection;
    itemToWorkAudioBlobs = itemToWork[1].data;

    if (itemToWorkId == null || itemToWorkSelection == null || itemToWorkAudioBlobs == null) {
      const itemToWorkErrorMessage = '[useNvoqQueue] Item to work has a null value, skipping';
      logger.warn(itemToWorkErrorMessage, {
        itemToWorkId,
        itemToWorkSelection,
        itemToWorkAudioBlobs: JSON.stringify(itemToWorkAudioBlobs),
        dictationQueueMap: JSON.stringify(dictationQueueMap),
        dictationQueueMapSize: dictationQueueMap.size,
      });
      dictationQueueMap.delete(itemToWorkId);
      setDictationQueueSizeState((prev) => ({
        ...prev,
        pendingDictations: dictationQueueMap.size,
      }));
      return;
    }

    const websocket = createNvoqWebsocket({
      id: itemToWorkId,
      selection: itemToWorkSelection,
      worklistItemId,
      additionalParams,
    });

    websocket.addEventListener('open', () => {
      setDictationQueueSizeState((prev) => ({
        ...prev,
        openWebsocketConnections: prev.openWebsocketConnections + 1,
      }));
    });

    websocket.addEventListener('close', () => {
      setDictationQueueSizeState((prev) => ({
        ...prev,
        openWebsocketConnections: prev.openWebsocketConnections - 1,
      }));
    });

    const newOpenItem = onNewWebsocketOpenSendQueue.current.get(itemToWorkId);
    // Add queued dictation to pending websocket queue because establishing a connection
    // is async and we don't want to lose any frames. Add any pending blobs that were from the
    // previous selection but captured after the done message to the front to make sure we don't
    // lose frames.
    pendingWebsocketDictationQueueMap.set(itemToWorkId, {
      selection: itemToWorkSelection,
      data: [...(newOpenItem?.data ?? []), ...itemToWorkAudioBlobs],
    });

    // Remove from the dictation queue
    dictationQueueMap.delete(itemToWorkId);

    // Add newly created websocket to the websocket map so we can keep up with its status
    // and send additional dictation to it
    nvoqWebsocketMap.set(itemToWorkId, websocket);

    setDictationQueueSizeState((prev) => ({
      ...prev,
      pendingWebsocketDictations: pendingWebsocketDictationQueueMap.size,
      pendingDictations: dictationQueueMap.size,
    }));

    // Clear any dictation that was captured at the previous selection after the done message was sent since it is
    // appended to the pendingWebsocketDictationQueueMap.
    onNewWebsocketOpenSendQueue.current.delete(itemToWorkId);

    // Create a range ref that will keep the user's intended target for this dictation
    // in sync with changes to the document.
    registerDictation(itemToWorkId, { selection: itemToWorkSelection });

    selectionToStepsQueueMap.set(itemToWorkSelection, []);
  };

const sendNvoqDoneMessage = ({
  id,
  socket,
  selectionToDoneMessageSentRef,
}: {
  id: string;
  socket: WebSocket;
  selectionToDoneMessageSentRef: Map<string, true>;
}) => {
  // Prevent sending multiple done messages for the same id
  const hasDoneMessageBeenSentForSelection = selectionToDoneMessageSentRef.get(id);
  if (hasDoneMessageBeenSentForSelection) return;

  if (socket?.readyState === 1) {
    performance.mark(marks.nvoq.Dictation.DoneMessageSent, id);
    logger.info('[useNvoqQueue] Stopping dictation (sending done message)', {
      logId: id,
      id,
      selectionToDoneMessageSent: JSON.stringify(selectionToDoneMessageSentRef),
    });
    socket.send(JSON.stringify(NVOQ_DONE_MESSAGE));
  }
  selectionToDoneMessageSentRef.set(id, true);
};

const sendNvoqBoundaryRequestMessage = ({
  id,
  socket,
  selection,
  duration,
}: {
  id: string;
  socket: WebSocket;
  selection: Nullable<Range>;
  duration: number;
}) => {
  if (socket?.readyState === 1) {
    logger.info('[useNvoqQueue] Requesting boundary', {
      logId: id,
      id,
      selection: selection != null ? stringifyRange(selection) : null,
    });
    const requestedBoundaryTime = (duration - REQUEST_BOUNDARY_DELAY) / 1000;

    socket.send(JSON.stringify(createNvoqBoundaryRequestMessage(requestedBoundaryTime)));
  }
};

export const useNvoqQueueInternal = ({
  maxNumberOfConnections,
  editor,
  isEnabled,
  isAlwaysEditable,
  nvoqID,
  nvoqAuthorization,
  onWebsocketError,
  onNvoqError,
  enablePicklistDictation,
  onExternalSubstitution,
}: UseNvoqQueueProps): UseNvoqQueue => {
  const analyticsData = useWorklistItemAnalytics({ context: APOLLO_QUERY_CONTEXT });
  const reportStatus = useRecoilValue<ReportStatuses>(reportStatusState);

  const setDictationQueueSizeState = useSetRecoilState(dictationQueueSizeState);
  const {
    processStableTextSerially,
    processHypothesisText,
    registerDictation,
    markDictationStopped,
    // This is only passed through to the returned object for testing purposes.
    dictationStableTextManagerMap,
    selectionToStepsQueueMap,
    mostRecentStableTextInserted,
    isMousePressed,
    setShouldSkipWebsocketCreationRef,
    skipWebsocketCreationOptionsRef,
  } = useTextProcessing({
    editor,
    enablePicklistDictation,
    onExternalSubstitution,
    context: APOLLO_QUERY_CONTEXT,
    isAlwaysEditable,
  });

  const decorateDispatch = useRangeMasksDispatch();
  const nvoqWebsocketMap = useRef(new Map<string, WebSocket>());
  const selectionToDoneMessageSentRef = useRef(new Map<string, true>());
  const onNewWebsocketOpenSendQueue = useRef(
    new Map<
      string,
      {
        selection: Range;
        data: Array<MessageToNvoq>;
      }
    >()
  );
  const dictationQueueMap = useRef(
    new Map<
      string,
      {
        selection: Range;
        data: Array<MessageToNvoq>;
      }
    >()
  );
  const pendingWebsocketDictationQueueMap = useRef(
    new Map<
      string,
      {
        selection: Range;
        data: Array<MessageToNvoq>;
      }
    >()
  );
  const nvoqEmitter = useRef(mitt<EmitterEvent>());

  const { audioInputDeviceLabel } = useRecoilValue(microphoneState);

  const createNvoqWebsocket = useMemo(
    () =>
      createNvoqWebsocketFactory(nvoqEmitter.current, {
        nvoqID,
        nvoqAuthorization,
      }),
    [nvoqID, nvoqAuthorization]
  );

  const { data: meData } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });

  useUnmount(() => {
    setDictationQueueSizeState((prev) => ({
      pendingDictations: 0,
      pendingWebsocketDictations: 0,
      openWebsocketConnections: prev.openWebsocketConnections,
    }));
  });

  const worklistItemId = analyticsData?.worklistItemId ?? '';
  const workNextItemInQueue = useMemo(
    () =>
      createWorkNextItemInQueue({
        createNvoqWebsocket,
        registerDictation,
        nvoqWebsocketMap: nvoqWebsocketMap.current,
        dictationQueueMap: dictationQueueMap.current,
        pendingWebsocketDictationQueueMap: pendingWebsocketDictationQueueMap.current,
        selectionToStepsQueueMap: selectionToStepsQueueMap.current,
        editor,
        onNewWebsocketOpenSendQueue: onNewWebsocketOpenSendQueue,
        worklistItemId,
        additionalParams: {
          userId: meData?.me.id,
          clinicId: meData?.me.clinic?.smid,
          worklistItemId: analyticsData?.worklistItemId,
          audioInputLabel: audioInputDeviceLabel,
          env: env?.DEPLOY_ENV,
          version: APP_VERSION,
          userAgent: navigator.userAgent,
        },
        setDictationQueueSizeState,
      }),
    [
      analyticsData?.worklistItemId,
      audioInputDeviceLabel,
      createNvoqWebsocket,
      registerDictation,
      editor,
      meData?.me.clinic?.smid,
      meData?.me.id,
      worklistItemId,
      setDictationQueueSizeState,
      selectionToStepsQueueMap,
    ]
  );

  useEffect(() => {
    // When recording is stopped, flush all the open sockets
    // forcing them to be resolved
    if (!isEnabled) {
      const sockets = nvoqWebsocketMap.current.entries();

      // If they stop recording clear any queued hypothesis text so that it doesn't auto append to
      // the next place they start recording
      const newWebsockets = onNewWebsocketOpenSendQueue.current.entries();
      for (const [id] of newWebsockets) {
        onNewWebsocketOpenSendQueue.current.delete(id);
      }

      for (const socketTuple of sockets) {
        const [id, socket] = socketTuple;
        if (socket.readyState === 1) {
          sendNvoqDoneMessage({
            id,
            socket,
            selectionToDoneMessageSentRef: selectionToDoneMessageSentRef.current,
          });
          mostRecentStableTextInserted.current = null;
        } else {
          // JUST BECAUSE THE USER IS NO LONGER RECORDING DOESN'T MEAN WE'RE DONE NVOQ'ING!
          //
          // This means the user has dictation awaiting to be worked, but stopped recording.
          // This appends a done message to the end of the pending queue items so that they
          // immediately close after sending all dictation.
          const queueDictation = pendingWebsocketDictationQueueMap.current.get(id);
          if (queueDictation) {
            pendingWebsocketDictationQueueMap.current.set(id, {
              ...queueDictation,
              data: [...queueDictation.data, NVOQ_DONE_MESSAGE],
            });
          }
        }
      }
    }
  }, [isEnabled, mostRecentStableTextInserted]);

  useEffect(() => {
    const onOpen = (event?: SelectionEventHandler) => {
      if (!event) return;
      const { id } = event;

      const socket = nvoqWebsocketMap.current.get(id);
      const dictation = pendingWebsocketDictationQueueMap.current.get(id);

      if (!socket) {
        const socketErrorMessage = '[useNvoqQueue] No socket found for id';
        createEditorWarning(socketErrorMessage);
        logger.warn(socketErrorMessage, {
          logId: id,
          id,
          pendingWebsocketDictationQueueMap: JSON.stringify(
            pendingWebsocketDictationQueueMap.current
          ),
          editor: partialEditor(editor),
        });
        return;
      }
      if (!dictation) {
        const dictationErrorMessage = '[useNvoqQueue] No dictation found for id';

        createEditorWarning(dictationErrorMessage);
        logger.warn(dictationErrorMessage, {
          logId: id,
          id,
          pendingWebsocketDictationQueueMap: JSON.stringify(
            pendingWebsocketDictationQueueMap.current
          ),
          editor: partialEditor(editor),
          socket: JSON.stringify(socket),
        });
        return;
      }

      // Send all the stored blobs
      dictation.data.forEach((d) => {
        if (isNvoqDoneMessage(d)) {
          sendNvoqDoneMessage({
            id,
            socket,
            selectionToDoneMessageSentRef: selectionToDoneMessageSentRef.current,
          });
        } else {
          socket?.send(d);
        }
      });

      // Remove after sending everything
      pendingWebsocketDictationQueueMap.current.delete(id);
      setDictationQueueSizeState((prev) => ({
        ...prev,
        pendingWebsocketDictations: pendingWebsocketDictationQueueMap.current.size,
      }));
      selectionToDoneMessageSentRef.current.delete(id);

      if (!isEnabled) {
        sendNvoqDoneMessage({
          id,
          socket,
          selectionToDoneMessageSentRef: selectionToDoneMessageSentRef.current,
        });
      }
    };

    nvoqEmitter.current.on('on_open', onOpen);

    const onStableText = async (event?: TextEventHandler) => {
      if (!event) return;
      const { msg, selection, id } = event;
      await processStableTextSerially(msg, selection, id);
    };

    nvoqEmitter.current.on('on_stable_text', onStableText);

    const onHypothesisText = (event?: TextEventHandler) => {
      if (!event) return;

      // if the mouse pressed, user could be highlighting text
      // don't let hypothesis text interfere with this
      if (isMousePressed === true) return;

      const { msg, selection, id } = event;

      processHypothesisText(msg, selection, id);
    };

    nvoqEmitter.current.on('on_hypothesis_text', onHypothesisText);

    const onWebsocketErrorF = () => onWebsocketError();
    nvoqEmitter.current.on('on_websocket_error', onWebsocketErrorF);

    const onNvoqErrorF = (
      ...args: [
        | undefined
        | {
            msg: NvoqError;
          },
      ]
    ) => onNvoqError(...args);
    nvoqEmitter.current.on('on_nvoq_error', onNvoqErrorF);

    // NOTE: OnClose can be called before text has been dictated for a given selection because
    // the stable text queue relies on async effects.
    const onClose = (event?: SelectionEventHandler) => {
      if (!event) return;
      const { id } = event;

      nvoqWebsocketMap.current.delete(id);

      // Work from the queue on closed if possible
      // I open at the close :D
      if (nvoqWebsocketMap.current.size < maxNumberOfConnections) {
        workNextItemInQueue();
      }

      // If there are no more dictations in the queue, pending websocket dictations in the queue, and
      // there are no more active websockets - cleanup decorations just in case we have stragglers. Should
      // never be needed unless we have bugs or the user does something weird with the selection while
      // dictating.
      if (
        nvoqWebsocketMap.current.size === 0 &&
        dictationQueueMap.current.size === 0 &&
        pendingWebsocketDictationQueueMap.current.size === 0
      ) {
        decorateDispatch((decorations) =>
          decorations.filter((d) => d[DICTATION_PLUGIN_ID] === true)
        );
      }
    };
    nvoqEmitter.current.on('on_close', onClose);

    const currentNvoqEmitter = nvoqEmitter.current;
    return () => {
      currentNvoqEmitter.off('on_open');
      currentNvoqEmitter.off('on_stable_text');
      currentNvoqEmitter.off('on_hypothesis_text');
      // This refs stores an event emitter, not DOM nodes
      currentNvoqEmitter.off('on_close');
      currentNvoqEmitter.off('on_nvoq_error');
      currentNvoqEmitter.off('on_websocket_error');
    };
  }, [
    editor,
    maxNumberOfConnections,
    workNextItemInQueue,
    decorateDispatch,
    onWebsocketError,
    onNvoqError,
    isEnabled,
    setDictationQueueSizeState,
    isMousePressed,
    processStableTextSerially,
    processHypothesisText,
  ]);

  useEffect(() => {
    const onProcessedDataAvailable = (event: RecorderEventPayloadMap['data_available']): void => {
      if (isPCMRecorderDataAvailableEvent(event)) {
        return;
      }

      const {
        data: blob,
        id,
        selectionRef: recorderSelectionRef,
        duration,
        setShouldSkipWebsocketCreation,
        shouldSkipWebsocketCreation,
        shouldUseASRPlex,
      }: RecorderDataAvailableEvent = event;
      let selection: Range | null;

      // if we should be using ASRPlex, we don't want to create websockets with the NvoqQueue.
      // This is necessary as long as NvoqQueueProvider and ASRPlexDictation providers are both wrapped.
      if (shouldUseASRPlex) {
        return;
      }

      if (
        PROVISIONAL_SUBMIT_STATUSES.includes(
          reportStatus as 'provisionalReportSubmit' | 'provisionalAddendumSubmit'
        )
      ) {
        selection = PROVISIONAL_SUBMIT_SELECTION;
      } else {
        selection = recorderSelectionRef?.current ?? editor.selection;
      }
      setShouldSkipWebsocketCreationRef.current = setShouldSkipWebsocketCreation;
      skipWebsocketCreationOptionsRef.current = {
        // for whether hypothesis text selection should use this selection or native from event
        shouldSkipWebsocketCreation,
      };

      if (!isEnabled) return;

      if (!selection || blob == null || blob.size === 0) {
        logger.info('[NvoqQueue] onProcessedDataAvailable: dropping audio packet', {
          hasSelection: selection != null,
          hasBlob: blob != null && blob.size > 0,
          reportStatus,
          id,
        });
        return;
      }

      // If the duration is under the minimum dictation duration, we
      // short-circuit and return early, storing the audio in the dictation
      // queue so that if it surpasses the minimum dictation duration, it
      // will be sent for transcription.
      //
      // The early return prevents us from opening a websocket connection even
      // if we have one available. This helps us avoid hitting our websocket
      // concurrency limit with short dictations that contain no actual audio
      // data to be transcribed.
      if (duration < MINIMUM_DICTATION_DURATION_MILLIS) {
        logger.info('[usNvoqQueue] Duration of dictation is under the minimum; returning early', {
          logId: stringifyRange(selection),
          editor: editor != null ? partialEditor(editor) : 'null',
          dictationQueueMap: JSON.stringify(dictationQueueMap.current),
          dictationQueueSize: dictationQueueMap.current.size,
          duration,
        });
        // Send it to the queue to be worked on
        const queueDictation = dictationQueueMap.current.get(id);
        if (queueDictation) {
          dictationQueueMap.current.set(id, {
            ...queueDictation,
            data: [...queueDictation.data, blob],
          });
        } else {
          dictationQueueMap.current.set(id, { selection, data: [blob] });
          setDictationQueueSizeState((prev) => ({
            ...prev,
            pendingDictations: dictationQueueMap.current.size,
          }));
        }
        return;
      }

      if (nvoqWebsocketMap.current.has(id)) {
        const websocket = nvoqWebsocketMap.current.get(id);

        if (selectionToDoneMessageSentRef.current.get(id) === true) {
          const queueDictation = pendingWebsocketDictationQueueMap.current.get(id);
          if (queueDictation) {
            pendingWebsocketDictationQueueMap.current.set(id, {
              ...queueDictation,
              data: [...queueDictation.data, blob],
            });
          } else {
            // enqueue dictation
            pendingWebsocketDictationQueueMap.current.set(id, { selection, data: [blob] });
            analytics.track(reporter.sys.dictationQueued);
          }
        } else if (websocket?.readyState === 1) {
          // If this selection has an active websocket, then send the blob.
          websocket?.send(blob);
        } else {
          if (editor.selection != null) {
            // If the websocket is not ready, send it to the queue to be worked
            // when it opens the queue will be ready based on the next branch
            const queueDictation = pendingWebsocketDictationQueueMap.current.get(id);
            if (queueDictation) {
              pendingWebsocketDictationQueueMap.current.set(id, {
                ...queueDictation,
                data: [...queueDictation.data, blob],
              });
            }
          }
        }
      } else if (dictationQueueMap.current.has(id)) {
        const queueDictation = dictationQueueMap.current.get(id);
        if (queueDictation) {
          dictationQueueMap.current.set(id, {
            ...queueDictation,
            data: [...queueDictation.data, blob],
          });
        }
      } else {
        dictationQueueMap.current.set(id, { selection, data: [blob] });
        setDictationQueueSizeState((prev) => ({
          ...prev,
          pendingDictations: dictationQueueMap.current.size,
        }));
      }

      if (
        nvoqWebsocketMap.current.size < maxNumberOfConnections &&
        dictationQueueMap.current.size > 0
      ) {
        workNextItemInQueue();
      }
    };

    recorderEmitter.on('data_available', onProcessedDataAvailable);

    const onStopRecording = ({
      id,
      selection,
      duration,
      shouldUseASRPlex,
    }: RecorderStopRecordingEvent) => {
      // If ASR Plex owns dictation, the NvoqQueue never populated its queues (see the matching
      // guard in onProcessedDataAvailable), so there is nothing to finalize here. Bail out to avoid
      // the spurious "[useNvoqQueue] No dictation queue found for selection" warning when both the
      // NvoqQueueProvider and ASRPlexDictation providers are mounted (Focus Mode visible + Plex).
      if (shouldUseASRPlex) {
        return;
      }

      markDictationStopped(id);
      const resolvedSelection = selection ?? editor.selection;
      if (resolvedSelection == null) {
        return;
      }
      const socket = nvoqWebsocketMap.current.get(id);
      // I think this is where the error is, its attaching a done message before we have received all the data available messages?
      if (socket != null && socket.readyState === 1) {
        sendNvoqDoneMessage({
          id,
          socket,
          selectionToDoneMessageSentRef: selectionToDoneMessageSentRef.current,
        });
        return;
      }

      const skip = duration < MINIMUM_DICTATION_DURATION_MILLIS;

      // The socket being null indicates that the concurrent connection limit to nVoq
      // has been reached and this dictation is still in the queue waiting to be picked
      // up. So we need to queue the done message at the end of the dictation queue so that
      // when the queue gets picked up it will send all its audio data and the done message.
      const queueDictation = pendingWebsocketDictationQueueMap.current.get(id);
      if (skip) {
        logger.info('[useNvoqQueue] Recording stopped before minimum dictation duration', {
          logId: id,
          duration,
          editor: editor != null ? partialEditor(editor) : 'null',
          dictationQueueMap: JSON.stringify(dictationQueueMap.current),
          dictationQueueSize: dictationQueueMap.current.size,
          pendingWebsocketDictations: JSON.stringify(pendingWebsocketDictationQueueMap.current),
          pendingWebsocketDictationsSize: pendingWebsocketDictationQueueMap.current.size,
        });
      }

      if (queueDictation) {
        if (skip) {
          pendingWebsocketDictationQueueMap.current.delete(id);
          setDictationQueueSizeState((prev) => ({
            ...prev,
            pendingWebsocketDictations: pendingWebsocketDictationQueueMap.current.size,
          }));
        } else {
          pendingWebsocketDictationQueueMap.current.set(id, {
            selection: resolvedSelection,
            data: [...queueDictation.data, NVOQ_DONE_MESSAGE],
          });
        }
      } else if (dictationQueueMap.current.has(id)) {
        if (skip) {
          dictationQueueMap.current.delete(id);
          setDictationQueueSizeState((prev) => ({
            ...prev,
            pendingDictations: dictationQueueMap.current.size,
          }));
        } else {
          const queueDictation = dictationQueueMap.current.get(id);
          if (queueDictation != null) {
            dictationQueueMap.current.set(id, {
              ...queueDictation,
              data: [...queueDictation.data, NVOQ_DONE_MESSAGE],
            });
          }
        }
      } else {
        // Hitting this path means we have a websocket queue that could become active later that will
        // never have a done message added to it. nVoq will eventually close this socket but it will delay
        // the user's transcript from completing.
        const warningMessage = '[useNvoqQueue] No dictation queue found for selection';
        logger.warn(warningMessage, {
          logId: id,
          editor: editor != null ? partialEditor(editor) : 'null',
          dictationQueueMap: JSON.stringify(dictationQueueMap.current),
          dictationQueueSize: dictationQueueMap.current.size,
        });
      }
    };

    recorderEmitter.on('stop_recording', onStopRecording);

    const onSilenceDetected = ({ id, selection, duration }: RecorderSilenceDetectedEvent) => {
      const socket = nvoqWebsocketMap.current.get(id);
      if (socket != null && socket.readyState === 1) {
        sendNvoqBoundaryRequestMessage({
          id,
          socket,
          selection,
          duration,
        });
        return;
      }
    };

    recorderEmitter.on('silence_detected', onSilenceDetected);

    return () => {
      recorderEmitter.off('stop_recording', onStopRecording);
      recorderEmitter.off('data_available', onProcessedDataAvailable);
      recorderEmitter.off('silence_detected', onSilenceDetected);
    };
  }, [
    editor,
    maxNumberOfConnections,
    workNextItemInQueue,
    isEnabled,
    reportStatus,
    setDictationQueueSizeState,
    setShouldSkipWebsocketCreationRef,
    skipWebsocketCreationOptionsRef,
    markDictationStopped,
  ]);

  return {
    nvoqEmitter: nvoqEmitter.current,
    nvoqWebsocketMap: nvoqWebsocketMap.current,
    dictationQueueMap: dictationQueueMap.current,
    pendingWebsocketDictationQueueMap: pendingWebsocketDictationQueueMap.current,
    dictationStableTextManagerMap: dictationStableTextManagerMap.current,
    selectionToStepsQueueMap: selectionToStepsQueueMap.current,
    selectionToDoneMessageSentRef: selectionToDoneMessageSentRef.current,
  };
};

export type NvoqQueueContextProps = Readonly<
  UseNvoqQueueProps & {
    children: React.ReactNode;
  }
>;
export type NvoqQueueContextBag = UseNvoqQueue;

const NvoqQueueContext = createContext<Nullable<NvoqQueueContextBag>>(undefined);

export const NvoqQueueProvider = ({
  children,
  ...rest
}: NvoqQueueContextProps): React.ReactElement => {
  const bag = useNvoqQueueInternal(rest);
  return <NvoqQueueContext.Provider value={bag}>{children}</NvoqQueueContext.Provider>;
};

export const useNvoqQueue = (): NvoqQueueContextBag => {
  const queueBag = useContext(NvoqQueueContext);

  if (!queueBag) {
    throw new Error('useNvoqQueue must be used within a NvoqQueueProvider.');
  }

  return queueBag;
};

NvoqQueueProvider.displayName = 'NvoqQueueProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(NvoqQueueProvider);
